// services/AdminServices/UIActionService.ts

import { ExecutionFlowModel } from "../../models/workFlow/ExecutionFlowModel";
import {
  RequestResponseDTO,
  RequestResponseOutDTO,
} from "../../models/workFlow/ConnectionModel";
import {
  AzureFunctionModel,
  FunctionParameter,
  UIActionFlowConfig,
} from "../../models/workFlow/UIActionModel";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Fetches all Azure Functions registered for a given connection.
 * Backend: GET /api/UIAction/GetAzureFunctions?connectionId={id}
 */
export const fetchAzureFunctions = async (
  accessToken: string,
  connectionId: number,
): Promise<AzureFunctionModel[]> => {
  try {debugger;
    const endpoint = `${API_URL}/api/ExecutionFlow/GetAzureFunctions?connectionId=${connectionId}`;
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Azure functions: ${response.statusText}`,
      );
    }

    const data = await response.json();
    console.log("Row Res:", data);
    return mapToAzureFunctions(
      Array.isArray(data) ? data : (data.response ?? []),
    );
  } catch (error) {
    console.error("Error in fetchAzureFunctions:", error);
    throw error;
  }
};

/**
 * Fetches the input and output parameters for a specific Azure Function.
 * Backend: GET /api/UIAction/GetFunctionParameters?functionId={id}
 */
export const fetchFunctionParameters = async (
  accessToken: string,
  functionId: number,
): Promise<{
  inputParameters: FunctionParameter[];
  outputParameters: FunctionParameter[];
}> => {
  try {
    const endpoint = `${API_URL}/api/UIAction/GetFunctionParameters?functionId=${functionId}`;
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch function parameters: ${response.statusText}`,
      );
    }

    const data = await response.json();
    const payload = data.response ?? data;
    return {
      inputParameters: mapToParameters(payload.inputParameters ?? []),
      outputParameters: mapToParameters(payload.outputParameters ?? []),
    };
  } catch (error) {
    console.error("Error in fetchFunctionParameters:", error);
    throw error;
  }
};

/**
 * Fetches request/response schema from backend for a selected Swagger endpoint.
 * Backend: POST /api/Connection/GetRequestResponse
 */
export const fetchSwaggerRequestResponse = async (
  accessToken: string,
  requestModel: RequestResponseDTO & { swaggerUrl?: string }
): Promise<{
  inputParameters: FunctionParameter[];
  outputParameters: FunctionParameter[];
}> => {
  const endpoint = `${API_URL}/api/Connection/GetRequestResponse`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestModel),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch request/response: ${response.statusText}`);
  }

  const data = await response.json();
  const payload = (data.response ?? data) as RequestResponseOutDTO;

  return {
    inputParameters: mapJsonToParameters(payload.inputJson, "input"),
    outputParameters: mapJsonToParameters(payload.outputJson, "output"),
  };
};

/**
 * Saves a UI Action flow configuration.
 * Serialises UIActionFlowConfig to workflowJson and calls create or update.
 * Backend: POST /api/UIAction/SaveUIActionFlow
 */
export const saveUIActionFlow = async (
  accessToken: string,
  config: UIActionFlowConfig,
  executionFlowId?: number,
  executionFlowName?: string,
  createdBy?: number,
): Promise<{
  success: boolean;
  executionFlowId?: number;
  message?: string;
}> => {
  try {
    const payload: ExecutionFlowModel & {
      uiActionConfig?: UIActionFlowConfig;
    } = {
      executionFlowId: executionFlowId ?? undefined,
      connectionId: config.connectionId,
      name: executionFlowName ?? "UI Action Flow",
      executionType: "UIAction",
      workflowJson: JSON.stringify(config),
      createdBy,
    };

    const isUpdate = !!executionFlowId;
    const endpoint = isUpdate
      ? `${API_URL}/api/ExecutionFlow/UpdateExecutionFlow`
      : `${API_URL}/api/ExecutionFlow/CreateExecutionFlow`;

    const response = await fetch(endpoint, {
      method: isUpdate ? "PUT" : "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();
    if (response.ok) {
      return {
        success: true,
        executionFlowId: result.executionFlowId ?? executionFlowId,
        message: result.message || "UI Action flow saved successfully",
      };
    }
    return {
      success: false,
      message: result.message || "Failed to save UI Action flow",
    };
  } catch (error) {
    console.error("Error in saveUIActionFlow:", error);
    return {
      success: false,
      message: "An error occurred while saving the flow",
    };
  }
};

/**
 * Loads an existing UI Action flow configuration for a connection.
 * Backend: GET /api/ExecutionFlow/GetExecutionByConnectionId?connectionId={id}
 */
export const loadUIActionFlow = async (
  accessToken: string,
  connectionId: number,
): Promise<{
  flowId?: number;
  flowName?: string;
  config?: UIActionFlowConfig;
} | null> => {
  try {
    const endpoint = `${API_URL}/api/ExecutionFlow/GetExecutionByConnectionId?connectionId=${connectionId}`;
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (response.status === 404) return null;
    if (!response.ok)
      throw new Error(`Failed to load flow: ${response.statusText}`);

    const data = await response.json();
    if (!data || !data.workflowJson) return null;

    const config: UIActionFlowConfig = JSON.parse(data.workflowJson);
    return {
      flowId: data.executionFlowId,
      flowName: data.name,
      config,
    };
  } catch (error) {
    console.error("Error in loadUIActionFlow:", error);
    return null;
  }
};

// ─── Mappers ─────────────────────────────────────────────────────────────────

const mapToAzureFunctions = (data: any[]): AzureFunctionModel[] =>
  data.map((item, index) => {debugger;
    const parsedFunctionId = Number(item.functionId);
    const functionId =
      Number.isFinite(parsedFunctionId) && parsedFunctionId > 0
        ? parsedFunctionId
        : -(index + 1); // fallback id for admin endpoints that don't return numeric functionId

    return {
      functionId,
      tenantId: Number(item.tenantId ?? 0),
      connectionId: Number(item.connectionId ?? 0),
      functionName: item.functionName ?? item.name ?? "Unnamed Function",
      functionUrl: item.functionUrl ?? item.invoke_url_template ?? item.href ?? "",
      requestGroup: extractRequestGroup(item.config?.bindings),
      functionCode: item.functionCode ?? item.config?.entryPoint ?? item.language ?? "",
      description: item.description ?? item.config?.entryPoint ?? "",
      inputParameters: mapToParameters(
        item.inputParameters ?? extractInputParametersFromBindings(item.config?.bindings)
      ),
      outputParameters: mapToParameters(
        item.outputParameters ?? extractOutputParametersFromBindings(item.config?.bindings)
      ),
    };
  });

const mapToParameters = (data: any[]): FunctionParameter[] =>
  data.map((item) => ({
    paramName: item.paramName ?? item.name ?? "",
    paramType: item.paramType ?? item.type ?? "string",
    isRequired: item.isRequired ?? false,
    description: item.description ?? "",
  }));

const extractInputParametersFromBindings = (bindings: any): FunctionParameter[] => {debugger;
  if (!Array.isArray(bindings)) return [];

  const routeParams = new Set<string>();

  bindings.forEach((binding) => {
    const isHttpTrigger =
      `${binding?.type ?? ""}`.toLowerCase() === "httptrigger" &&
      `${binding?.direction ?? ""}`.toLowerCase() === "in";
    if (!isHttpTrigger) return;

    const route = `${binding?.route ?? ""}`;
    const routeParamRegex = /\{([^}:]+)(?::[^}]+)?\}/g;
    let match: RegExpExecArray | null;
    while ((match = routeParamRegex.exec(route)) !== null) {
      const key = (match?.[1] ?? "").trim();
      if (key) routeParams.add(key);
    }
  });

  return Array.from(routeParams).map((paramName) => ({
    paramName,
    paramType: "string",
    isRequired: true,
    description: "Route parameter from Azure Function binding",
  }));
};

const extractOutputParametersFromBindings = (bindings: any): FunctionParameter[] => {debugger;
  if (!Array.isArray(bindings)) return [];

  const hasHttpOut = bindings.some(
    (binding) =>
      `${binding?.type ?? ""}`.toLowerCase() === "http" &&
      `${binding?.direction ?? ""}`.toLowerCase() === "out"
  );

  if (!hasHttpOut) return [];

  return [
    {
      paramName: "response",
      paramType: "object",
      isRequired: false,
      description: "HTTP response payload returned by the function",
    },
  ];
};

const extractRequestGroup = (bindings: any): string => {
  if (!Array.isArray(bindings)) return "get";
  const trigger = bindings.find(
    (binding) =>
      `${binding?.type ?? ""}`.toLowerCase() === "httptrigger" &&
      `${binding?.direction ?? ""}`.toLowerCase() === "in"
  );
  const methods = trigger?.methods;
  if (!Array.isArray(methods) || methods.length === 0) return "get";
  return `${methods[0] ?? "get"}`.toLowerCase();
};

const mapJsonToParameters = (
  jsonText: string | undefined,
  fallbackPrefix: "input" | "output"
): FunctionParameter[] => {
  if (!jsonText) return [];

  try {
    const parsed = JSON.parse(jsonText);
    if (Array.isArray(parsed)) {
      return parsed.map((item, index) => ({
        paramName: `${fallbackPrefix}_${index + 1}`,
        paramType: inferParamType(item),
        isRequired: false,
        description: "",
      }));
    }

    if (parsed && typeof parsed === "object") {
      return Object.keys(parsed).map((key) => ({
        paramName: key,
        paramType: inferParamType(parsed[key]),
        isRequired: false,
        description: "",
      }));
    }
  } catch {
    // If response is not JSON, return one raw placeholder parameter.
  }

  return [
    {
      paramName: `${fallbackPrefix}Payload`,
      paramType: "string",
      isRequired: false,
      description: "Raw payload",
    },
  ];
};

const inferParamType = (value: any): string => {
  if (Array.isArray(value)) return "array";
  if (value === null || value === undefined) return "string";
  if (typeof value === "object") return "object";
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "string";
};
